"use client";

import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useCallback, useMemo } from "react";

type ParamValue = string | number | boolean | undefined | null;

export function useUrlState() {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const isNullValue = (value: unknown) =>
    value === undefined ||
    value === null ||
    value === "" ||
    value === "all" ||
    (Array.isArray(value) && value.length === 0);

  const getLatestParams = useCallback(() => {
    if (typeof window === "undefined")
      return new URLSearchParams(searchParams.toString());
    return new URLSearchParams(window.location.search);
  }, [searchParams]);

  const setParams = useCallback(
    (updates: Record<string, ParamValue | ParamValue[]>) => {
      const params = getLatestParams();

      Object.entries(updates).forEach(([key, value]) => {
        if (isNullValue(value)) {
          params.delete(key);
        } else if (Array.isArray(value)) {
          params.set(key, value.join("_"));
        } else {
          params.set(key, String(value));
        }
      });

      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    },
    [getLatestParams, pathname, router],
  );

  const setParam = useCallback(
    (key: string, value: ParamValue) => {
      setParams({ [key]: value });
    },
    [setParams],
  );

  const clearParams = useCallback(() => {
    router.replace(pathname, { scroll: false });
  }, [pathname, router]);

  const allParams = useMemo(() => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }, [searchParams]);

  const pagination = useMemo(() => {
    const currentPage = Number(allParams.page) || 1;
    return {
      currentPage,
      onPageChange: (page: number) => setParam("page", page),
    };
  }, [allParams.page, setParam]);

  return {
    getParam: (key: string) => searchParams.get(key),
    setParam,
    setParams,
    clearParams,
    allParams,
    pagination,
  };
}
